import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { UtensilsCrossed, GlassWater, Check, Loader2, ChefHat, Soup } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const TIMES = [];
for (let h = 19; h <= 20; h++) for (let m = 0; m < 60; m += 15) { if (h === 20 && m > 30) break; TIMES.push(`${h}h${m === 0 ? "00" : m}`); }

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",").replace(",00", "")} €`;
const frLong = (s) => { try { return new Date(s + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };

export default function MealOrderPage() {
  const { settings } = useSite();
  const menu = useMemo(() => settings.bocaux_menu || { courses: [], drinks: [] }, [settings.bocaux_menu]);
  const galettes = useMemo(() => settings.galettes_menu || { sections: [] }, [settings.galettes_menu]);
  const hasGalettes = (galettes.sections || []).length > 0;

  const [menuKind, setMenuKind] = useState("cuisine_pere"); // "cuisine_pere" | "galettes"
  const [service, setService] = useState("dine_in"); // "dine_in" | "room_drinks"
  const [qty, setQty] = useState({});
  const [form, setForm] = useState({ name: "", room: "", date: "", time: "", email: "", note: "" });
  const todayStr = new Date().toISOString().slice(0, 10);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formSel, setFormSel] = useState({});
  const [formulaCart, setFormulaCart] = useState([]);
  const formulas = menu.formulas || [];

  const isGalettes = menuKind === "galettes";
  const isRoom = !isGalettes && service === "room_drinks";
  const setQ = (key, v) => setQty((q) => ({ ...q, [key]: Math.max(0, v) }));

  const pickChoice = (choice) => {
    setQty({}); setFormulaCart([]);
    if (choice === "galettes") { setMenuKind("galettes"); setService("dine_in"); }
    else if (choice === "room") { setMenuKind("cuisine_pere"); setService("room_drinks"); }
    else { setMenuKind("cuisine_pere"); setService("dine_in"); }
  };

  const lines = useMemo(() => {
    const out = [];
    if (isGalettes) {
      (galettes.sections || []).forEach((sec) => (sec.items || []).forEach((it, i) => {
        const k = `g-${sec.key}-${i}`; const n = qty[k] || 0;
        if (n > 0) out.push({ name: `${sec.label} — ${it.name}`, qty: n, price: it.price });
      }));
      return out;
    }
    if (!isRoom) {
      formulaCart.forEach((f) => out.push({ name: `${f.label} (${f.detail})`, qty: 1, price: f.price }));
      (menu.courses || []).forEach((c) => (c.items || []).forEach((it, i) => {
        const k = `c-${c.key}-${i}`; const n = qty[k] || 0;
        if (n > 0) out.push({ name: `${c.label} — ${it.name}`, qty: n, price: c.price });
      }));
    }
    (menu.drinks || []).forEach((d, i) => {
      const k = `d-${i}`; const n = qty[k] || 0;
      if (n > 0) out.push({ name: `Boisson — ${d.name}`, qty: n, price: d.price });
    });
    return out;
  }, [qty, menu, galettes, isRoom, isGalettes, formulaCart]);

  const total = lines.reduce((s, l) => s + l.qty * l.price, 0);

  const getSel = (f, ck) => formSel[f.key]?.[ck] || ((menu.courses || []).find((c) => c.key === ck)?.items?.[0]?.name || "");
  const setSel = (fk, ck, v) => setFormSel((s) => ({ ...s, [fk]: { ...(s[fk] || {}), [ck]: v } }));
  const addFormulaToCart = (f) => {
    const detail = (f.courses || []).map((ck) => { const c = (menu.courses || []).find((x) => x.key === ck); return `${c?.label}: ${getSel(f, ck)}`; }).join(" · ");
    setFormulaCart((c) => [...c, { label: f.label, price: f.price, detail }]);
    toast.success("Formule ajoutée");
  };
  const removeFormulaLine = (idx) => setFormulaCart((c) => c.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.name || !form.room) { toast.error("Nom et numéro de chambre sont requis."); return; }
    if (!form.date) { toast.error("Choisissez la date de votre commande."); return; }
    if (!isRoom && !form.time) { toast.error("Choisissez l'heure du service."); return; }
    if (!lines.length) { toast.error(isRoom ? "Sélectionnez au moins une boisson." : "Sélectionnez au moins un plat ou une boisson."); return; }
    setBusy(true);
    try {
      await api.post("/meal-orders", { ...form, service, menu: menuKind, lines });
      setSent(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur, réessayez."); }
    finally { setBusy(false); }
  };

  const Stepper = ({ k }) => (
    <div className="flex items-center gap-2">
      <button onClick={() => setQ(k, (qty[k] || 0) - 1)} className="w-7 h-7 rounded-full border border-ink-400/30 text-ink-600" data-testid={`minus-${k}`}>−</button>
      <span className="w-6 text-center text-sm font-medium" data-testid={`qty-${k}`}>{qty[k] || 0}</span>
      <button onClick={() => setQ(k, (qty[k] || 0) + 1)} className="w-7 h-7 rounded-full bg-brand text-white" data-testid={`plus-${k}`}>+</button>
    </div>
  );

  if (sent) return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md text-center" data-testid="meal-order-success">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><Check className="text-emerald-600" /></div>
        <h1 className="font-heading text-3xl text-ink-900 mb-2">Commande envoyée !</h1>
        <p className="text-ink-600">
          Merci {form.name}. {isRoom
            ? <>Nous vous livrons votre commande <b>en chambre {form.room}</b> le <b>{frLong(form.date)}</b>. Règlement en carte ou en espèces, en chambre.</>
            : <>Votre commande sera servie le <b>{frLong(form.date)}</b> à <b>{form.time}</b>. Règlement en CB sur place après le service.</>}
        </p>
      </div>
    </div>
  );

  const Card = ({ id, icon: Icon, title, desc, onClick, active }) => (
    <button type="button" onClick={onClick}
      className={`flex-1 text-left rounded-2xl border p-4 transition-colors ${active ? "border-brand bg-brand-50" : "border-ink-400/20 hover:border-brand/40"}`}
      data-testid={id}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-9 h-9 rounded-full flex items-center justify-center ${active ? "bg-brand text-white" : "bg-beige text-brand"}`}><Icon size={18} /></span>
        <span className="font-heading text-lg text-ink-900">{title}</span>
      </div>
      <p className="text-xs text-ink-500 leading-relaxed">{desc}</p>
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="meal-order-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>Commander · La Bégaudière</title></Helmet>
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl text-ink-900">Commander</h1>
        <p className="text-ink-500 text-sm mt-2">Choisissez votre menu, puis composez votre commande.</p>
      </div>

      {/* Sélecteur : 3 options distinctes */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="menu-selector">
        <Card id="menu-cuisine_pere" icon={ChefHat} title="La Cuisine de Mon Père" desc="Plats maison en bocal du chef : entrées, plats, desserts. Servi sur place, sur réservation." onClick={() => pickChoice("cuisine")} active={!isGalettes && !isRoom} />
        {hasGalettes && (
          <Card id="menu-galettes" icon={Soup} title="Galettes & Crêpes" desc="Galettes bretonnes, crêpes desserts et boissons — sur réservation à l'avance." onClick={() => pickChoice("galettes")} active={isGalettes} />
        )}
        <Card id="menu-room" icon={GlassWater} title="Boisson en chambre" desc="Commandez uniquement une boisson, livrée en chambre dès que possible." onClick={() => pickChoice("room")} active={!isGalettes && isRoom} />
      </div>

      {/* ============ MENU LA CUISINE DE MON PÈRE ============ */}
      {!isGalettes && (
        <>
          <div className="text-center mb-8">
            {isRoom ? (
              <img src="/begaudiere-galettes-logo.webp" alt="La Bégaudière · Drinks" className="w-64 sm:w-80 object-contain mx-auto" data-testid="room-logo" />
            ) : (
              <>
                <img src="/cuisine-mon-pere-logo.webp" alt="La Cuisine de Mon Père" className="w-32 h-32 sm:w-40 sm:h-40 object-contain mx-auto mb-3" data-testid="cuisine-logo" />
                <p className="text-xs text-ink-600 leading-relaxed bg-beige/60 rounded-lg p-4 text-left" data-testid="meal-story">
                  Le chef <b>Thierry Teffaine</b> et sa femme <b>Magali</b> ont marqué la scène gastronomique dinannaise pendant plus de 17 ans avec leur restaurant <i>Les 3 Lunes</i>, devenu une véritable référence locale. Cuisinier passionné et formateur reconnu (cuisson sous-vide, basse température, techniques douces), Thierry partage son savoir-faire depuis des années. De là est née une idée simple : rendre la cuisine du chef accessible partout, avec le même niveau d'exigence que dans un grand restaurant.
                </p>
              </>
            )}
          </div>

          {/* Formules (uniquement en mode sur place) */}
          {!isRoom && formulas.length > 0 && (
            <div className="mb-8" data-testid="formulas-section">
              <div className="text-center mb-4">
                <h2 className="font-heading text-2xl text-ink-900">Nos formules</h2>
                <p className="text-sm text-ink-500">Composez votre repas et profitez du meilleur prix.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {formulas.map((f) => (
                  <div key={f.key} className={`relative rounded-2xl border p-4 ${f.popular ? "border-brand ring-1 ring-brand/30 bg-brand-50/40" : "border-ink-400/20"}`} data-testid={`formula-${f.key}`}>
                    {f.popular ? <span className="absolute -top-2.5 right-4 rounded-full bg-brand text-white text-[11px] px-2.5 py-0.5">Le plus choisi</span> : null}
                    <div className="flex items-baseline justify-between mb-1">
                      <h3 className="font-heading text-lg text-ink-900">{f.label}</h3>
                      <span className="font-heading text-brand text-lg">{eur(f.price)}</span>
                    </div>
                    {f.desc ? <p className="text-xs text-ink-500 mb-3">{f.desc}</p> : null}
                    <div className="space-y-2 mb-3">
                      {(f.courses || []).map((ck) => {
                        const c = (menu.courses || []).find((x) => x.key === ck);
                        if (!c) return null;
                        return (
                          <div key={ck}>
                            <label className="text-xs text-ink-500">{c.label}</label>
                            <select value={getSel(f, ck)} onChange={(e) => setSel(f.key, ck, e.target.value)} className="w-full rounded-lg border border-ink-400/25 px-2 py-1.5 text-sm bg-white" data-testid={`formula-${f.key}-${ck}`}>
                              {(c.items || []).map((it, i) => <option key={i} value={it.name}>{it.name}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                    <button onClick={() => addFormulaToCart(f)} className="w-full rounded-full bg-brand text-white py-2 text-sm font-medium hover:bg-brand-600" data-testid={`formula-add-${f.key}`}>Ajouter cette formule</button>
                  </div>
                ))}
              </div>
              {formulaCart.length > 0 && (
                <div className="mt-4 rounded-xl bg-beige/60 p-3 space-y-1.5" data-testid="formula-cart">
                  {formulaCart.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-ink-800 min-w-0"><b>{f.label}</b> <span className="text-ink-400">· {f.detail}</span></span>
                      <span className="flex items-center gap-2 shrink-0"><b>{eur(f.price)}</b><button onClick={() => removeFormulaLine(i)} className="text-red-500 hover:text-red-700" data-testid={`formula-remove-${i}`}>✕</button></span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-center text-xs text-ink-400 mt-5">— ou à la carte —</p>
            </div>
          )}

          {/* Menu à la carte (uniquement en mode sur place) */}
          {!isRoom && (menu.courses || []).map((c) => (
            <div key={c.key} className="mb-6">
              <div className="flex items-baseline justify-between border-b border-black/10 pb-2 mb-3">
                <h2 className="font-heading text-xl text-ink-900">{c.label}</h2>
                <span className="font-heading text-brand">{eur(c.price)}</span>
              </div>
              <div className="space-y-3">
                {(c.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {it.img ? <img src={it.img} alt={it.name} loading="lazy" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : null}
                      <div className="min-w-0"><p className="text-sm font-medium text-ink-900">{it.name}</p><p className="text-xs text-ink-500">{it.desc}</p></div>
                    </div>
                    <Stepper k={`c-${c.key}-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Boissons */}
          <div className="mb-8">
            <div className="flex items-baseline justify-between border-b border-black/10 pb-2 mb-3">
              <h2 className="font-heading text-xl text-ink-900">Boissons</h2>
              <span className="text-xs text-ink-400">{menu.drinks_note || "33 cl"}</span>
            </div>
            <div className="space-y-3">
              {(menu.drinks || []).map((d, i) => (
                <div key={i} className="flex items-center justify-between gap-4">
                  <p className="text-sm font-medium text-ink-900">{d.name} <span className="text-ink-400 font-normal">· {eur(d.price)}</span></p>
                  <Stepper k={`d-${i}`} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ============ MENU GALETTES & CRÊPES ============ */}
      {isGalettes && (
        <>
          <div className="text-center mb-6">
            <img src="/begaudiere-galettes-logo.webp" alt="La Bégaudière · Galettes, Crêpes & Drinks" className="w-64 sm:w-80 object-contain mx-auto mb-3" data-testid="galettes-logo" />
            <p className="text-sm text-ink-500 mt-1">Saveurs bretonnes et bonnes humeurs · sur réservation à l'avance.</p>
          </div>
          {(galettes.sections || []).map((sec) => (
            <div key={sec.key} className="mb-6" data-testid={`galettes-section-${sec.key}`}>
              <h3 className="font-heading text-xl text-ink-900 border-b border-black/10 pb-2 mb-3">{sec.label}</h3>
              <div className="space-y-3">
                {(sec.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-900">{it.name} <span className="text-ink-400 font-normal">· {eur(it.price)}</span></p>
                      {it.desc ? <p className="text-xs text-ink-500">{it.desc}</p> : null}
                    </div>
                    <Stepper k={`g-${sec.key}-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ============ FORMULAIRE COMMANDE ============ */}
      <div className="rounded-2xl bg-beige/60 p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Votre nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-ink-400/25 px-3 py-2 text-sm" data-testid="meal-name" />
          <input placeholder="Numéro de chambre *" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="rounded-lg border border-ink-400/25 px-3 py-2 text-sm" data-testid="meal-room" />
          <label className="flex flex-col text-xs text-ink-500">Date souhaitée *
            <input type="date" value={form.date} min={todayStr} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-lg border border-ink-400/25 px-3 py-2 text-sm bg-white text-ink-900" data-testid="meal-date" />
          </label>
          {!isRoom && (
            <select value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-lg border border-ink-400/25 px-3 py-2 text-sm bg-white" data-testid="meal-time">
              <option value="">Heure du service (19h–20h30) *</option>
              {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <input placeholder="E-mail (confirmation)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border border-ink-400/25 px-3 py-2 text-sm" data-testid="meal-email" />
        </div>
        <textarea placeholder="Remarque (allergies, etc.)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm" data-testid="meal-note" />
        <div className="flex items-center justify-between pt-1">
          <span className="font-heading text-lg text-ink-900" data-testid="meal-total">Total : {eur(total)}</span>
          <button onClick={submit} disabled={busy} className="rounded-full bg-brand text-white px-7 py-3 font-medium hover:bg-brand-600 disabled:opacity-60 inline-flex items-center gap-2" data-testid="meal-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Commander
          </button>
        </div>
        <p className="text-xs text-ink-400">
          {isRoom
            ? "Aucun paiement en ligne — livraison en chambre dès que possible, réglement en carte bancaire ou en espèces, en chambre."
            : "Aucun paiement en ligne — réglement en carte bancaire sur place, après le service."}
        </p>
      </div>
    </div>
  );
}
